import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import {FeatureName} from '@contentlab/types/project';
import {
  OnboardingStep,
  ONBOARDING_STEPS_ORDER,
} from '@contentlab/views/contentlab/pages/onboarding-page/onboardingSteps';

export const USER_TYPES = {
  PRIVATE_USER: 'Personal use',
  SMALL_MEDIUM_BUSINESS: 'Small/medium business',
  LARGE_ENTERPRISE: 'Large company (100+ employees)',
} as const;

export type UserTypeOption = (typeof USER_TYPES)[keyof typeof USER_TYPES];

export type OnboardingWizardState = {
  step: OnboardingStep;
  previousSteps: OnboardingStep[];
  hoveredFeature: FeatureName | null;
  consentFormAccepted: boolean;
  selectedFeature: FeatureName | null;
  selectedUserType: UserTypeOption | null;
  forceOnboarding: boolean;
};

const onboardingWizardStore = create<OnboardingWizardState>()(
  immer(
    (): OnboardingWizardState => ({
      step: ONBOARDING_STEPS_ORDER[0],
      previousSteps: [],
      hoveredFeature: null,
      consentFormAccepted: false,
      selectedFeature: null,
      selectedUserType: null,
      forceOnboarding: false,
    })
  )
);

const setStep = (step: OnboardingStep) => {
  onboardingWizardStore.setState((draft) => {
    draft.previousSteps.push(draft.step);
    draft.step = step;
  });
};

const goToPreviousStep = () => {
  onboardingWizardStore.setState((draft) => {
    if (draft.previousSteps.length > 0) {
      const previousStep = draft.previousSteps.pop();
      if (!previousStep) {
        return;
      }
      draft.step = previousStep;
    }
  });
};

const setSelectedFeature = (feature: FeatureName) => {
  onboardingWizardStore.setState((draft) => {
    draft.selectedFeature = feature;
  });
};

const setHoveredFeature = (feature: FeatureName | null) => {
  onboardingWizardStore.setState((draft) => {
    draft.hoveredFeature = feature;
  });
};

const setConsentFormAccepted = (isAccepted: boolean) => {
  onboardingWizardStore.setState((draft) => {
    draft.consentFormAccepted = isAccepted;
  });
};

const setSelectedUserType = (userType: UserTypeOption) => {
  onboardingWizardStore.setState((draft) => {
    draft.selectedUserType = userType;
  });
};

const setForceOnboarding = (value: boolean) => {
  onboardingWizardStore.setState((draft) => {
    draft.forceOnboarding = value;
  });
};

export const onboardingWizardActions = {
  setStep,
  goToPreviousStep,
  setSelectedFeature,
  setHoveredFeature,
  setConsentFormAccepted,
  setSelectedUserType,
  setForceOnboarding,
};

export default onboardingWizardStore;
